import React, { useState, useEffect } from 'react';
import Icon from '../../common/Icon/Icon';

/**
 * The binder filter rail - the Search page's rail language pointed at
 * your own cards. Every option is an always-visible pill carrying the
 * count of copies behind it, so the rail doubles as the binder's
 * composition read-out (sets touched, rarity split, foil count)
 * without a single extra fetch. No dropdowns, no collapsed groups.
 *
 * Zones, top to bottom:
 *   Sets    - selected sets as chips + the Search rail's type-ahead
 *             (a binder touches sets fast; pills crowded)
 *   Rarity  - rarities present, common to special
 *   Finish  - finishes you actually hold ('any' wishes included)
 *   Price   - min-max on the entry-finish price
 * No Tools panel: adding cards is Search's job (quick-add lives on
 * every card there), so the binder page keeps filters only.
 *
 * State contract: filters live in Binders.js; this renders them and
 * calls back. Only the mobile sheet's open state is local. Mobile
 * (<=760px) reuses the Search rail's bottom-sheet chrome wholesale:
 * a Filters button under the search box, scrim, internal scroll,
 * "Show results" to close.
 */

const RARITY_ORDER = ['common', 'uncommon', 'rare', 'mythic', 'special'];

function BinderRail({ facets, setNames, filters, activeCount, onPatch, onToggleList, onReset }) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [setQuery, setSetQuery] = useState('');

    // Mobile sheet open = the page behind must not scroll
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    const pill = (key, label, count, on, onClick, { cap = false } = {}) => (
        <button
            key={key}
            className={`os-pill${on ? ' on' : ''}${cap ? ' cap' : ''}`}
            onClick={onClick}
        >
            {label}{count != null && <span className="bn-n">{count}</span>}
        </button>
    );

    const setEntries = Object.entries(facets.sets).sort((a, b) => b[1] - a[1]);
    const rarities = RARITY_ORDER.filter(r => facets.rarities[r]);
    const finishes = Object.keys(facets.finishes).sort();

    // Same contract as the Search rail's set picker: 2+ chars, top 8
    const setMatches = setQuery.trim().length >= 2
        ? setEntries.filter(([code]) => {
            const q = setQuery.trim().toLowerCase();
            return (setNames[code] || code).toLowerCase().includes(q) || code.toLowerCase().includes(q);
        }).slice(0, 8)
        : [];

    return (
        <>
            {/* Mobile: a visible Filters button (same as Search) opens
                the rail as a bottom sheet; hidden on desktop */}
            <div className="os-mobilebar">
                <button className={`os-mobile-filters${activeCount ? ' on' : ''}`} onClick={() => setMobileOpen(true)}>
                    <Icon name="sliders" /> Filters{activeCount > 0 ? ` · ${activeCount}` : ''}
                </button>
            </div>
            {mobileOpen && <div className="os-rail-scrim" onClick={() => setMobileOpen(false)} />}

            <aside className={`os-rail${mobileOpen ? ' open' : ''}`}>
                <div className="os-sheet-head">
                    <span className="os-rail-title">
                        Filters{activeCount > 0 ? ` (${activeCount})` : ''}
                    </span>
                    <button
                        className="os-sheet-close"
                        onClick={() => setMobileOpen(false)}
                        aria-label="Close filters"
                    >
                        ✕
                    </button>
                </div>

                <div className="os-rail-panel">
                    <div className="os-railhead">
                        <span className="os-rail-title">Filters</span>
                        {activeCount > 0 && (
                            <button
                                className="os-rail-clearbtn"
                                onClick={() => { setSetQuery(''); onReset(); }}
                            >
                                ↺ Reset
                            </button>
                        )}
                    </div>

                    {setEntries.length > 0 && (
                        <div className="os-zone">
                            <div className="os-rail-title">Sets</div>
                            {filters.sets.length > 0 && (
                                <div className="os-rail-chips">
                                    {filters.sets.map(code => (
                                        <button
                                            key={code}
                                            className="os-rail-chip"
                                            title="Remove"
                                            onClick={() => onToggleList('sets', code)}
                                        >
                                            {setNames[code] || code} ×
                                        </button>
                                    ))}
                                </div>
                            )}
                            <input
                                className="os-rail-input"
                                placeholder="Search your sets…"
                                value={setQuery}
                                onChange={e => setSetQuery(e.target.value)}
                            />
                            {setMatches.length > 0 && (
                                <div className="os-rail-setlist">
                                    {setMatches.map(([code, count]) => (
                                        <button
                                            key={code}
                                            className="os-rail-setrow"
                                            onClick={() => {
                                                if (!filters.sets.includes(code)) {
                                                    onToggleList('sets', code);
                                                }
                                                setSetQuery('');
                                            }}
                                        >
                                            {setNames[code] || code}<span className="bn-n">{count}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {rarities.length > 0 && (
                        <div className="os-zone">
                            <div className="os-rail-title">Rarity</div>
                            <div className="os-pillrow">
                                {rarities.map(r =>
                                    pill(r, r, facets.rarities[r],
                                        filters.rarities.includes(r),
                                        () => onToggleList('rarities', r), { cap: true }))}
                            </div>
                        </div>
                    )}

                    {finishes.length > 0 && (
                        <div className="os-zone">
                            <div className="os-rail-title">Finish</div>
                            <div className="os-pillrow">
                                {finishes.map(f =>
                                    pill(f, f, facets.finishes[f],
                                        filters.finishes.includes(f),
                                        () => onToggleList('finishes', f), { cap: true }))}
                            </div>
                        </div>
                    )}

                    <div className="os-zone">
                        <div className="os-rail-title">Price</div>
                        <div className="os-rail-price">
                            <input
                                type="number" min="0" placeholder="Min"
                                value={filters.priceMin}
                                onChange={e => onPatch({ priceMin: e.target.value })}
                            />
                            <span>–</span>
                            <input
                                type="number" min="0" placeholder="Max"
                                value={filters.priceMax}
                                onChange={e => onPatch({ priceMax: e.target.value })}
                            />
                        </div>
                    </div>
                </div>

                {/* Mobile sheet only: apply-and-close */}
                <button className="os-sheet-done" onClick={() => setMobileOpen(false)}>
                    Show results
                </button>
            </aside>
        </>
    );
}

export default BinderRail;
